import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

interface Business {
  id: string; name: string; slug: string; description?: string;
  badge?: string; badgeConfig?: { label: string; color: string; emoji: string };
  address?: string; city?: string; phone?: string; email?: string;
  website?: string; logo?: string; coverImage?: string; category?: string;
  instagram?: string; facebook?: string; tiktok?: string; whatsapp?: string;
  gallery?: string[]; currency: string;
  services: Service[]; staff: Staff[]; events: FitEvent[];
}
interface Service { id: string; name: string; description?: string; duration: number; price: number; color: string; }
interface Staff { id: string; firstName: string; lastName: string; bio?: string; avatar?: string; }
interface FitEvent { id: string; title: string; description?: string; startTime: string; endTime: string; maxCapacity: number; image?: string; price?: number; location?: string; service?: { name: string; price: number }; staff?: { firstName: string; lastName: string }; _count?: { bookings: number }; }

const SYM: Record<string, string> = { EUR: '€', USD: '$', GBP: '£', MAD: 'DH' };
function fmt(d: string) { return new Date(d).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Madrid' }); }
function fmtT(d: string) { return new Date(d).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' }); }

export function PublicBusiness() {
  const { slug } = useParams<{ slug: string }>();
  const [biz, setBiz] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState<'eventos' | 'galeria' | 'resenas'>('eventos');

  // Booking state
  const [selectedEvent, setSelectedEvent] = useState<FitEvent | null>(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const [reviews, setReviews] = useState<any[]>([]);
  const [reviewAvg, setReviewAvg] = useState(0);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  const loadReviews = async (businessId: string) => {
    try {
      const r = await axios.get(`${API_URL}/reviews/${businessId}`);
      setReviews(r.data.data.reviews || []);
      setReviewAvg(r.data.data.average || 0);
    } catch {}
  };

  const handleSubmitReview = async () => {
    if (!isLoggedIn) { alert('Debes iniciar sesión para dejar una reseña'); return; }
    setSubmittingReview(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(`${API_URL}/reviews/${biz?.id}`, { rating: reviewRating, comment: reviewComment }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowReviewForm(false);
      setReviewComment('');
      setReviewRating(5);
      if (biz) loadReviews(biz.id);
      alert('✅ ¡Reseña publicada!');
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Error al publicar la reseña');
    } finally {
      setSubmittingReview(false);
    }
  };

  const [eventFilter, setEventFilter] = useState<'todos'|'hoy'|'semana'|'mes'|'gratis'|'pago'>('todos');

  const filteredEvents = (events: FitEvent[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(today.getTime() + 86400000);
    const endOfWeek = new Date(today.getTime() + 7 * 86400000);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return events.filter(e => {
      const d = new Date(e.startTime);
      switch(eventFilter) {
        case 'hoy': return d >= today && d < endOfToday;
        case 'semana': return d >= today && d < endOfWeek;
        case 'mes': return d >= today && d < endOfMonth;
        case 'gratis': return !e.price || e.price === 0;
        case 'pago': return e.price && e.price > 0;
        default: return true;
      }
    });
  };

  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authFirstName, setAuthFirstName] = useState('');
  const [authLastName, setAuthLastName] = useState('');
  const [authPhone, setAuthPhone] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setIsLoggedIn(true);
        setUserEmail(payload.email || '');
      } catch {}
    }
    axios.get(`${API_URL}/businesses/public/${slug}`)
      .then(r => setBiz(r.data.data.business))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleAuth = async () => {
    if (!authEmail || !authPassword) { alert('Rellena email y contraseña'); return; }
    setAuthLoading(true);
    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
      const body = authMode === 'login'
        ? { email: authEmail, password: authPassword }
        : { email: authEmail, password: authPassword, firstName: authFirstName, lastName: authLastName, phone: authPhone, role: 'CLIENT' };
      const r = await axios.post(`${API_URL}${endpoint}`, body);
      const { accessToken, refreshToken } = r.data.data;
      localStorage.setItem('accessToken', accessToken);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      setIsLoggedIn(true);
      setUserEmail(authEmail);

      // Auto-confirmar inscripción si hay evento seleccionado
      if (selectedEvent) {
        setSending(true);
        try {
          await axios.post(`${API_URL}/event-bookings/${selectedEvent.id}`, {}, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          setDone(true);
          setSelectedEvent(null);
          alert('✅ ¡Cuenta creada e inscripción confirmada!');
        } catch (e2: any) {
          alert(e2?.response?.data?.message || 'Error al inscribirse');
        } finally {
          setSending(false);
        }
      }
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Error al autenticar');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setIsLoggedIn(false);
    setUserEmail('');
  };

  const handleConfirmBooking = async () => {
    if (!selectedEvent) return;
    const eventId = selectedEvent.id;
    setSending(true);
    try {
      const token = localStorage.getItem('accessToken');
      const isFree = !selectedEvent.price || Number(selectedEvent.price) === 0;

      if (isFree) {
        // Inscripción gratuita directa
        await axios.post(`${API_URL}/event-bookings/${eventId}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDone(true);
        setSelectedEvent(null);
        alert('✅ ¡Inscripción confirmada! Te esperamos en el evento.');
      } else {
        // Pago online via Stripe del negocio
        try {
          const r = await axios.post(`${API_URL}/event-payments/checkout/${eventId}`, {
            successUrl: `${window.location.href}?paid=true`,
            cancelUrl: window.location.href,
          }, { headers: { Authorization: `Bearer ${token}` } });
          window.location.href = r.data.data.checkoutUrl;
        } catch (e: any) {
          const msg = e?.response?.data?.message || '';
          if (msg.includes('no tiene pagos online')) {
            // Negocio sin Stripe — inscribir sin pago y pagar presencialmente
            await axios.post(`${API_URL}/event-bookings/${eventId}`, {}, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setDone(true);
            setSelectedEvent(null);
            alert(`✅ ¡Plaza reservada! El pago de ${selectedEvent.price}€ se realizará en el negocio.`);
          } else {
            alert(msg || 'Error al procesar el pago');
          }
        }
      }
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Error al realizar la inscripción');
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: '3px solid #e2e8f0', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }}></div>
        <p style={{ color: '#64748b' }}>Cargando...</p>
      </div>
    </div>
  );

  if (notFound || !biz) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, background: '#f8fafc' }}>
      <div style={{ fontSize: 64 }}>🔍</div>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>Negocio no encontrado</h1>
      <a href="https://fitpulse.es" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>← Volver a FitPulse</a>
    </div>
  );

  const sym = SYM[biz.currency] || '€';
  const gallery = Array.isArray(biz.gallery) ? biz.gallery : [];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Inter', sans-serif; background: #f1f5f9; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .pub-navbar { background: white; border-bottom: 1px solid #e2e8f0; padding: 0 5%; height: 56px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 50; }
        .pub-cover { position: relative; height: 260px; background: linear-gradient(135deg, #1e293b, #0f172a); overflow: hidden; }
        .pub-cover img { width: 100%; height: 100%; object-fit: cover; opacity: .7; }
        .pub-header { background: white; border-bottom: 1px solid #e2e8f0; }
        .pub-header-inner { max-width: 960px; margin: 0 auto; padding: 0 24px; display: flex; align-items: flex-end; gap: 20px; position: relative; transform: translateY(-36px); margin-bottom: -8px; }
        .pub-logo { width: 80px; height: 80px; border-radius: 18px; background: linear-gradient(135deg, #3b82f6, #8b5cf6); border: 4px solid white; display: flex; align-items: center; justify-content: center; font-size: 1.8rem; font-weight: 800; color: white; overflow: hidden; flex-shrink: 0; box-shadow: 0 8px 24px rgba(0,0,0,.15); }
        .pub-logo img { width: 100%; height: 100%; object-fit: cover; }
        .pub-info { flex: 1; padding-bottom: 14px; padding-top: 8px; }
        .pub-name { font-size: 1.5rem; font-weight: 800; color: #1e293b; margin-bottom: 6px; margin-top: 8px; }
        .pub-meta { display: flex; gap: 16px; flex-wrap: wrap; }
        .pub-meta-item { font-size: .78rem; color: #64748b; display: flex; align-items: center; gap: 4px; text-decoration: none; }
        .pub-tabs { background: white; border-bottom: 1px solid #e2e8f0; position: sticky; top: 56px; z-index: 40; }
        .pub-tabs-inner { max-width: 960px; margin: 0 auto; padding: 0 24px; display: flex; }
        .pub-tab { padding: 14px 20px; font-size: .85rem; font-weight: 600; color: #64748b; border: none; background: none; cursor: pointer; border-bottom: 2px solid transparent; transition: all .2s; white-space: nowrap; }
        .pub-tab.active { color: #3b82f6; border-bottom-color: #3b82f6; }
        .pub-body { max-width: 960px; margin: 0 auto; padding: 32px 24px; animation: fadeUp .3s ease; }
        .ev-card { background: white; border-radius: 16px; border: 1px solid #e2e8f0; overflow: hidden; display: flex; margin-bottom: 14px; transition: transform .2s, box-shadow .2s; }
        .ev-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,.08); }
        .ev-date-col { background: linear-gradient(135deg, #3b82f6, #6366f1); color: white; padding: 20px 14px; display: flex; flex-direction: column; align-items: center; justify-content: center; min-width: 75px; text-align: center; }
        .ev-day { font-size: 1.8rem; font-weight: 800; line-height: 1; }
        .ev-mon { font-size: .65rem; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; opacity: .85; margin-top: 2px; }
        .ev-body { flex: 1; padding: 18px 20px; display: flex; flex-direction: column; justify-content: space-between; }
        .ev-title { font-size: 1rem; font-weight: 700; color: #1e293b; margin-bottom: 6px; }
        .ev-meta { font-size: .78rem; color: #64748b; margin-bottom: 10px; }
        .ev-tags { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 14px; }
        .tag { font-size: .7rem; font-weight: 600; padding: 3px 10px; border-radius: 100px; }
        .tag-gray { background: #f1f5f9; color: #475569; }
        .tag-blue { background: #dbeafe; color: #1d4ed8; }
        .tag-green { background: #dcfce7; color: #15803d; }
        .btn-book { background: #3b82f6; color: white; border: none; padding: 10px 20px; border-radius: 10px; font-size: .82rem; font-weight: 700; cursor: pointer; transition: background .2s; white-space: nowrap; font-family: 'Inter', sans-serif; }
        .btn-book:hover { background: #2563eb; }
        .svc-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 16px; }
        .svc-card { background: white; border-radius: 14px; padding: 20px; border: 1px solid #e2e8f0; }
        .svc-bar { height: 4px; border-radius: 100px; margin-bottom: 14px; }
        .svc-name { font-size: .95rem; font-weight: 700; color: #1e293b; margin-bottom: 6px; }
        .svc-desc { font-size: .8rem; color: #64748b; line-height: 1.5; margin-bottom: 14px; }
        .svc-footer { display: flex; align-items: center; justify-content: space-between; }
        .svc-price { font-size: 1.2rem; font-weight: 800; color: #1e293b; }
        .svc-dur { font-size: .72rem; color: #94a3b8; margin-top: 2px; }
        .staff-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 16px; }
        .staff-card { background: white; border-radius: 14px; padding: 24px 16px; text-align: center; border: 1px solid #e2e8f0; }
        .staff-av { width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(135deg, #3b82f6, #8b5cf6); display: flex; align-items: center; justify-content: center; font-size: 1.4rem; font-weight: 700; color: white; margin: 0 auto 12px; overflow: hidden; }
        .staff-av img { width: 100%; height: 100%; object-fit: cover; }
        .gal-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 10px; }
        .gal-item { aspect-ratio: 1; border-radius: 10px; overflow: hidden; cursor: pointer; }
        .gal-item img { width: 100%; height: 100%; object-fit: cover; transition: transform .3s; }
        .gal-item:hover img { transform: scale(1.05); }
        .empty { text-align: center; padding: 56px 24px; color: #94a3b8; }
        .lightbox { position: fixed; inset: 0; background: rgba(0,0,0,.92); z-index: 200; display: flex; align-items: center; justify-content: center; animation: fadeUp .2s ease; }
        .lightbox img { max-width: 90vw; max-height: 90vh; border-radius: 10px; }
        .lbx-close { position: absolute; top: 20px; right: 24px; color: white; font-size: 2rem; cursor: pointer; background: none; border: none; }
        .modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 20px; backdrop-filter: blur(4px); }
        .modal-box { background: white; border-radius: 20px; padding: 28px; max-width: 440px; width: 100%; box-shadow: 0 40px 100px rgba(0,0,0,.25); max-height: 90vh; overflow-y: auto; animation: fadeUp .2s ease; }
        .modal-title { font-size: 1.15rem; font-weight: 800; color: #1e293b; margin-bottom: 4px; }
        .modal-sub { font-size: .82rem; color: #64748b; margin-bottom: 20px; }
        .modal-summary { background: #f8fafc; border-radius: 12px; padding: 14px; margin-bottom: 20px; }
        .modal-row { display: flex; justify-content: space-between; font-size: .83rem; padding: 4px 0; }
        .modal-label { color: #64748b; }
        .modal-value { font-weight: 600; color: #1e293b; }
        .fi { width: 100%; padding: 11px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: .9rem; color: #1e293b; outline: none; font-family: 'Inter', sans-serif; margin-bottom: 10px; }
        .fi:focus { border-color: #3b82f6; }
        .fl { display: block; font-size: .78rem; font-weight: 600; color: #475569; margin-bottom: 4px; }
        .btn-confirm { width: 100%; padding: 13px; border-radius: 10px; border: none; background: #3b82f6; color: white; font-weight: 700; font-size: .92rem; cursor: pointer; font-family: 'Inter', sans-serif; margin-top: 8px; transition: background .2s; }
        .btn-confirm:hover:not(:disabled) { background: #2563eb; }
        .btn-confirm:disabled { background: #93c5fd; cursor: not-allowed; }
        .btn-cancel { width: 100%; padding: 11px; border-radius: 10px; border: 1px solid #e2e8f0; background: white; color: #64748b; font-weight: 600; font-size: .88rem; cursor: pointer; font-family: 'Inter', sans-serif; margin-top: 8px; }
        .tab-btns { display: flex; gap: 8px; margin-bottom: 16px; }
        .tab-btn { flex: 1; padding: 9px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: white; font-weight: 700; font-size: .82rem; cursor: pointer; font-family: 'Inter', sans-serif; transition: all .2s; }
        .tab-btn.active { background: #3b82f6; color: white; border-color: #3b82f6; }
        .pub-footer { background: #1e293b; color: #64748b; text-align: center; padding: 24px; font-size: .78rem; margin-top: 48px; }
        .pub-footer a { color: #3b82f6; text-decoration: none; }
        @media(max-width:600px){ .ev-card { flex-direction: column; } .ev-date-col { flex-direction: row; gap: 6px; padding: 10px 14px; min-width: unset; } .pub-tab { padding: 12px 14px; font-size: .78rem; } }
      `}</style>

      {/* NAVBAR */}
      <div className="pub-navbar">
        <a href="https://fitpulse.es" style={{ fontWeight: 800, fontSize: '1.1rem', color: '#3b82f6', textDecoration: 'none' }}>● FitPulse</a>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isLoggedIn ? (
            <a href="/mis-inscripciones" style={{ background: '#3b82f6', color: 'white', fontWeight: 700, fontSize: '.82rem', padding: '7px 16px', borderRadius: 8, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
              📅 Mis inscripciones
            </a>
          ) : (
            <>
              <a href="/login" style={{ color: '#64748b', fontWeight: 600, fontSize: '.82rem', textDecoration: 'none' }}>Iniciar sesión</a>
              <a href="/register" style={{ background: '#3b82f6', color: 'white', fontWeight: 700, fontSize: '.82rem', padding: '7px 16px', borderRadius: 8, textDecoration: 'none' }}>Registrarse</a>
            </>
          )}
        </div>
      </div>

      {/* COVER */}
      <div className="pub-cover" style={{background: biz.coverImage ? 'transparent' : `linear-gradient(135deg, #${Math.abs(biz.name.charCodeAt(0) * 99999).toString(16).slice(0,6).padEnd(6,'3b82f6')} 0%, #1e293b 100%)`}}>
        {biz.coverImage && <img src={biz.coverImage} alt={biz.name} />}
        {!biz.coverImage && (
          <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:8,opacity:.15}}>
            <div style={{fontSize:'5rem'}}>🏢</div>
          </div>
        )}
      </div>

      {/* HEADER */}
      <div className="pub-header">
        <div className="pub-header-inner">
          <div className="pub-logo">
            {biz.logo ? <img src={biz.logo} alt={biz.name} /> : biz.name.charAt(0).toUpperCase()}
          </div>
          <div className="pub-info">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              {biz.badge && biz.badgeConfig && (
                <span style={{ background: biz.badge === 'premium' ? '#fef3c7' : biz.badge === 'pro' ? '#dbeafe' : '#dcfce7', color: biz.badge === 'premium' ? '#92400e' : biz.badge === 'pro' ? '#1e40af' : '#15803d', fontSize: '.7rem', fontWeight: 700, padding: '2px 10px', borderRadius: 100 }}>
                  {biz.badgeConfig.emoji} {biz.badgeConfig.label}
                </span>
              )}
              {biz.category && <span style={{ fontSize: '.72rem', fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '.06em' }}>{biz.category}</span>}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
              <div className="pub-name" style={{margin:0}}>{biz.name.charAt(0).toUpperCase() + biz.name.slice(1)}</div>
              {biz.badge && (
                <div title={biz.badge === 'premium' ? 'Negocio Premium' : biz.badge === 'pro' ? 'Negocio Pro' : 'Negocio Verificado'}
                  style={{width:24,height:24,borderRadius:'50%',background: biz.badge==='premium'?'#f59e0b':biz.badge==='pro'?'#6366f1':'#3b82f6',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,boxShadow:`0 2px 8px ${biz.badge==='premium'?'rgba(245,158,11,.4)':biz.badge==='pro'?'rgba(99,102,241,.4)':'rgba(59,130,246,.4)'}`}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                {biz.whatsapp && (
                  <a href={`https://wa.me/${(biz as any).whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
                    style={{width:30,height:30,borderRadius:'50%',background:'#25D366',display:'flex',alignItems:'center',justifyContent:'center',textDecoration:'none',flexShrink:0,boxShadow:'0 2px 8px rgba(37,211,102,.4)'}}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </a>
                )}
                {(biz as any).instagram && (
                  <a href={`https://instagram.com/${(biz as any).instagram}`} target="_blank" rel="noreferrer"
                    style={{width:30,height:30,borderRadius:'50%',background:'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)',display:'flex',alignItems:'center',justifyContent:'center',textDecoration:'none',flexShrink:0,boxShadow:'0 2px 8px rgba(220,39,67,.4)'}}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  </a>
                )}
                {(biz as any).facebook && (
                  <a href={(biz as any).facebook} target="_blank" rel="noreferrer"
                    style={{width:30,height:30,borderRadius:'50%',background:'#1877F2',display:'flex',alignItems:'center',justifyContent:'center',textDecoration:'none',flexShrink:0,boxShadow:'0 2px 8px rgba(24,119,242,.4)'}}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                )}
                {(biz as any).tiktok && (
                  <a href={`https://tiktok.com/@${(biz as any).tiktok}`} target="_blank" rel="noreferrer"
                    style={{width:30,height:30,borderRadius:'50%',background:'#000',display:'flex',alignItems:'center',justifyContent:'center',textDecoration:'none',flexShrink:0,boxShadow:'0 2px 8px rgba(0,0,0,.3)'}}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.19 8.19 0 004.79 1.53V6.78a4.85 4.85 0 01-1.02-.09z"/></svg>
                  </a>
                )}
              </div>
            </div>
            <div className="pub-meta">
              {biz.city && <span className="pub-meta-item">📍 {biz.city}</span>}
              {biz.phone && <a href={`tel:${biz.phone}`} className="pub-meta-item">📞 {biz.phone}</a>}
              {biz.email && <a href={`mailto:${biz.email}`} className="pub-meta-item">✉️ {biz.email}</a>}
              {biz.instagram && <a href={`https://instagram.com/${biz.instagram}`} target="_blank" rel="noreferrer" className="pub-meta-item">📸 @{biz.instagram}</a>}
              {biz.whatsapp && <a href={`https://wa.me/${biz.whatsapp}`} target="_blank" rel="noreferrer" className="pub-meta-item">💬 WhatsApp</a>}
            </div>
          </div>
        </div>
        {biz.description && (
          <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 24px 18px', fontSize: '.88rem', color: '#475569', lineHeight: 1.6 }}>
            {biz.description}
          </div>
        )}
      </div>

      {/* TABS */}
      <div className="pub-tabs">
        <div className="pub-tabs-inner">
          {([['eventos', `📅 Eventos (${biz.events.length})`], ['resenas', `⭐ Reseñas (${reviews.length})`], ...(gallery.length > 0 ? [['galeria', `🖼️ Galería`]] : [])] as [string, string][]).map(([k, l]) => (
            <button key={k} className={`pub-tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k as any)}>{l}</button>
          ))}
        </div>
      </div>

      {/* BODY */}
      <div className="pub-body">
        {done && (
          <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 12, padding: '16px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, color: '#15803d', fontWeight: 600 }}>
            ✅ ¡Inscripción completada! Te esperamos en el evento.
          </div>
        )}

        {/* FILTROS EVENTOS */}
        {tab === 'eventos' && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
            {([['todos','🗓 Todos'],['hoy','⚡ Hoy'],['semana','📅 Esta semana'],['mes','🗃 Este mes'],['gratis','🆓 Gratis'],['pago','💶 De pago']] as [string,string][]).map(([k,l]) => (
              <button key={k} onClick={() => setEventFilter(k as any)} style={{ padding: '8px 16px', borderRadius: 100, border: '1.5px solid', fontSize: '.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all .2s', background: eventFilter === k ? '#3b82f6' : 'white', color: eventFilter === k ? 'white' : '#64748b', borderColor: eventFilter === k ? '#3b82f6' : '#e2e8f0' }}>
                {l}
              </button>
            ))}
          </div>
        )}

        {/* EVENTOS */}
        {tab === 'eventos' && (
          filteredEvents(biz.events).length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
              {filteredEvents(biz.events).map(e => {
                const d = new Date(e.startTime);
                const isFree = !e.price || e.price === 0;
                return (
                  <div key={e.id} style={{ background: 'white', borderRadius: 18, overflow: 'hidden', border: '1px solid #e2e8f0', transition: 'transform .2s, box-shadow .2s', cursor: 'pointer' }}
                    onMouseEnter={el => { (el.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (el.currentTarget as HTMLElement).style.boxShadow = '0 12px 32px rgba(0,0,0,.1)'; }}
                    onMouseLeave={el => { (el.currentTarget as HTMLElement).style.transform = ''; (el.currentTarget as HTMLElement).style.boxShadow = ''; }}>
                    
                    {/* Imagen o gradiente */}
                    <div style={{ position: 'relative', height: 180, background: 'linear-gradient(135deg, #1e293b, #334155)', overflow: 'hidden' }}>
                      {e.image
                        ? <img src={e.image} alt={e.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, #${Math.floor(Math.random()*16777215).toString(16).padStart(6,'0')}22, #1e293b)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4rem' }}>📅</div>
                      }
                      {/* Fecha flotante */}
                      <div style={{ position: 'absolute', top: 12, left: 12, background: 'white', borderRadius: 12, padding: '8px 12px', textAlign: 'center', minWidth: 52, boxShadow: '0 4px 12px rgba(0,0,0,.15)' }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#1e293b', lineHeight: 1 }}>{d.getDate()}</div>
                        <div style={{ fontSize: '.62rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em' }}>{d.toLocaleDateString('es-ES', { month: 'short' })}</div>
                      </div>
                      {/* Badge precio */}
                      <div style={{ position: 'absolute', top: 12, right: 12, background: isFree ? '#dcfce7' : '#dbeafe', color: isFree ? '#15803d' : '#1d4ed8', fontSize: '.72rem', fontWeight: 800, padding: '5px 12px', borderRadius: 100 }}>
                        {isFree ? '🆓 Gratis' : `💶 ${e.price}${sym}`}
                      </div>
                    </div>

                    {/* Contenido */}
                    <div style={{ padding: '18px 18px 16px' }}>
                      <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#1e293b', marginBottom: 8, lineHeight: 1.3 }}>{e.title}</h3>
                      {e.description && <p style={{ fontSize: '.8rem', color: '#64748b', lineHeight: 1.5, marginBottom: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{e.description}</p>}
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem', color: '#475569' }}>
                          🕐 {fmtT(e.startTime)} – {fmtT(e.endTime)}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem', color: '#475569' }}>
                          📅 {fmt(e.startTime)}
                        </div>
                        {e.location && <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem', color: '#475569' }}>📍 {e.location}</div>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem', color: (() => { const left = e.maxCapacity - (e._count?.bookings || 0); return left <= 0 ? '#dc2626' : left <= 3 ? '#f59e0b' : '#475569'; })() }}>
                          👥 {(() => { const left = e.maxCapacity - (e._count?.bookings || 0); return left <= 0 ? '❌ Sin plazas' : left <= 3 ? `⚠️ Solo ${left} plazas` : `${left} plazas disponibles`; })()}
                        </div>
                      </div>

                      {(() => {
                        const left = e.maxCapacity - (e._count?.bookings || 0);
                        return left <= 0 ? (
                          <button disabled style={{ width: '100%', padding: '12px', borderRadius: 12, fontSize: '.9rem', fontWeight: 700, background: '#f1f5f9', color: '#94a3b8', border: 'none', cursor: 'not-allowed' }}>
                            ❌ Sin plazas disponibles
                          </button>
                        ) : (
                          <button
                            className="btn-book"
                            style={{ width: '100%', padding: '12px', borderRadius: 12, fontSize: '.9rem', fontWeight: 700 }}
                            onClick={() => setSelectedEvent(e)}>
                            {left <= 3 ? `⚡ ¡Solo ${left} plazas! Inscribirme` : 'Inscribirme →'}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <div className="empty"><div style={{ fontSize: 48, marginBottom: 14 }}>📅</div><p style={{ fontWeight: 600, color: '#475569', marginBottom: 6 }}>{eventFilter === 'todos' ? 'Sin eventos próximos' : `Sin eventos ${eventFilter === 'hoy' ? 'hoy' : eventFilter === 'semana' ? 'esta semana' : eventFilter === 'mes' ? 'este mes' : eventFilter === 'gratis' ? 'gratuitos' : 'de pago'}`}</p><p style={{ fontSize: '.82rem' }}>Prueba con otro filtro</p></div>
        )}





        {/* GALERÍA */}
        {tab === 'galeria' && (
          <div className="gal-grid">
            {gallery.map((img, i) => (
              <div key={i} className="gal-item" onClick={() => setLightbox(img)}>
                <img src={img} alt={`Foto ${i + 1}`} loading="lazy" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* LIGHTBOX */}
      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <button className="lbx-close" onClick={() => setLightbox(null)}>✕</button>
          <img src={lightbox} alt="foto" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* MODAL INSCRIPCIÓN */}
      {selectedEvent && (
        <div className="modal-bg" onClick={() => setSelectedEvent(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Inscribirse al evento</div>
            <div className="modal-sub">Confirma tu plaza</div>

            <div className="modal-summary">
              <div className="modal-row"><span className="modal-label">Evento</span><span className="modal-value">{selectedEvent.title}</span></div>
              <div className="modal-row"><span className="modal-label">Fecha</span><span className="modal-value">{fmt(selectedEvent.startTime)}</span></div>
              <div className="modal-row"><span className="modal-label">Hora</span><span className="modal-value">{fmtT(selectedEvent.startTime)} – {fmtT(selectedEvent.endTime)}</span></div>
              <div className="modal-row"><span className="modal-label">Precio</span><span className="modal-value">{selectedEvent.price && selectedEvent.price > 0 ? `${selectedEvent.price}${sym}` : 'Gratis'}</span></div>
            </div>

            {isLoggedIn ? (
              <>
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '12px 14px', marginBottom: 14, fontSize: '.83rem', color: '#15803d', display: 'flex', alignItems: 'center', gap: 8 }}>
                  ✅ Conectado como <strong>{userEmail}</strong>
                  <button onClick={handleLogout} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '.72rem', fontFamily: 'Inter, sans-serif' }}>Salir</button>
                </div>
                <button
                  className="btn-confirm"
                  disabled={sending}
                  onClick={handleConfirmBooking}
                >
                  {sending ? '⏳ Procesando...' :
                   selectedEvent.price && Number(selectedEvent.price) > 0
                     ? `💳 Pagar ${selectedEvent.price}€ e inscribirme`
                     : '✅ Confirmar inscripción gratuita'}
                </button>
                <button className="btn-cancel" onClick={() => setSelectedEvent(null)}>Cancelar</button>
              </>
            ) : (
              <>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: '.82rem', color: '#1d4ed8' }}>
                  🔐 Necesitas una cuenta FitPulse para inscribirte. Es gratis.
                </div>
                <div className="tab-btns">
                  <button className={`tab-btn ${authMode === 'login' ? 'active' : ''}`} onClick={() => setAuthMode('login')}>Iniciar sesión</button>
                  <button className={`tab-btn ${authMode === 'register' ? 'active' : ''}`} onClick={() => setAuthMode('register')}>Crear cuenta</button>
                </div>
                {authMode === 'register' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 4 }}>
                    <div><label className="fl">Nombre *</label><input className="fi" placeholder="Ahmed" value={authFirstName} onChange={e => setAuthFirstName(e.target.value)} /></div>
                    <div><label className="fl">Apellidos *</label><input className="fi" placeholder="García" value={authLastName} onChange={e => setAuthLastName(e.target.value)} /></div>
                  </div>
                )}
                <label className="fl">Email *</label>
                <input className="fi" type="email" placeholder="tu@email.com" value={authEmail} onChange={e => setAuthEmail(e.target.value)} />
                <label className="fl">Contraseña *</label>
                <input className="fi" type="password" placeholder="Mínimo 8 caracteres con números" value={authPassword} onChange={e => setAuthPassword(e.target.value)} />
                {authMode === 'register' && (
                  <>
                    <label className="fl">Teléfono</label>
                    <input className="fi" placeholder="+34 600 000 000" value={authPhone} onChange={e => setAuthPhone(e.target.value)} />
                  </>
                )}
                <button className="btn-confirm" disabled={authLoading} onClick={handleAuth}>
                  {authLoading ? '⏳ Procesando...' : authMode === 'login' ? 'Entrar e inscribirme' : 'Registrarme e inscribirme'}
                </button>
                <button className="btn-cancel" onClick={() => setSelectedEvent(null)}>Cancelar</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* RESEÑAS */}
      {tab === 'resenas' && (
        <div className="pub-body">
          {/* Header reseñas */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 900, color: '#1e293b', lineHeight: 1 }}>{reviewAvg.toFixed(1)}</div>
                <div style={{ display: 'flex', gap: 2, justifyContent: 'center', margin: '4px 0' }}>
                  {[1,2,3,4,5].map(s => <span key={s} style={{ fontSize: '1rem', color: s <= Math.round(reviewAvg) ? '#f59e0b' : '#e2e8f0' }}>★</span>)}
                </div>
                <div style={{ fontSize: '.72rem', color: '#64748b' }}>{reviews.length} reseñas</div>
              </div>
            </div>
            <button onClick={() => setShowReviewForm(!showReviewForm)}
              style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 10, fontWeight: 700, fontSize: '.85rem', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
              ✍️ Escribir reseña
            </button>
          </div>

          {/* Formulario reseña */}
          {showReviewForm && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 16, padding: 20, marginBottom: 24 }}>
              <p style={{ fontWeight: 700, fontSize: '.9rem', color: '#1e293b', marginBottom: 12 }}>Tu valoración</p>
              <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                {[1,2,3,4,5].map(s => (
                  <button key={s} onClick={() => setReviewRating(s)}
                    style={{ fontSize: '1.8rem', background: 'none', border: 'none', cursor: 'pointer', color: s <= reviewRating ? '#f59e0b' : '#e2e8f0', transition: 'color .15s' }}>★</button>
                ))}
              </div>
              <textarea
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
                placeholder="Cuéntanos tu experiencia..."
                style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: '.88rem', fontFamily: 'Inter, sans-serif', resize: 'none', outline: 'none', marginBottom: 12 }}
                rows={3}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowReviewForm(false)}
                  style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', fontWeight: 600, fontSize: '.85rem', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
                  Cancelar
                </button>
                <button onClick={handleSubmitReview} disabled={submittingReview}
                  style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: '#3b82f6', color: 'white', fontWeight: 700, fontSize: '.85rem', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
                  {submittingReview ? 'Publicando...' : '✅ Publicar reseña'}
                </button>
              </div>
            </div>
          )}

          {/* Lista reseñas */}
          {reviews.length > 0 ? reviews.map((r, i) => (
            <div key={i} style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', padding: '18px 20px', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '.9rem', flexShrink: 0 }}>
                  {r.user?.firstName?.charAt(0)}{r.user?.lastName?.charAt(0)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '.88rem', color: '#1e293b' }}>{r.user?.firstName} {r.user?.lastName}</div>
                  <div style={{ display: 'flex', gap: 2 }}>
                    {[1,2,3,4,5].map(s => <span key={s} style={{ fontSize: '.85rem', color: s <= r.rating ? '#f59e0b' : '#e2e8f0' }}>★</span>)}
                  </div>
                </div>
                <div style={{ fontSize: '.72rem', color: '#94a3b8' }}>
                  {new Date(r.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                </div>
              </div>
              {r.comment && <p style={{ fontSize: '.85rem', color: '#475569', lineHeight: 1.6, margin: 0 }}>{r.comment}</p>}
            </div>
          )) : (
            <div style={{ textAlign: 'center', padding: '40px 24px', color: '#94a3b8' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>⭐</div>
              <p style={{ fontWeight: 600, color: '#475569', marginBottom: 6 }}>Sin reseñas todavía</p>
              <p style={{ fontSize: '.82rem' }}>Sé el primero en valorar este negocio</p>
            </div>
          )}
        </div>
      )}

      <div className="pub-footer">
        Powered by <a href="https://fitpulse.es">FitPulse</a> · La plataforma fitness de España
      </div>
    </>
  );
}
